/**
 * ValidatorPanel.ts
 * UI panel that displays validation results from the Validator.
 * Renders into the right sidebar as a collapsible list of violations.
 */

import { Validator } from './Validator.ts'
import type { Violation } from './Validator.ts'
import type { GridCell, GridPos } from '../types/OctileTypes.ts'

export interface ValidatorPanelCallbacks {
    onCellsHighlighted?: (cells: GridPos[]) => void
}

export class ValidatorPanel {
    private container: HTMLElement
    private validator = new Validator()
    private violations: Violation[] = []
    private callbacks: ValidatorPanelCallbacks

    constructor(container: HTMLElement, callbacks: ValidatorPanelCallbacks = {}) {
        this.container = container
        this.callbacks = callbacks
        this.renderEmpty()
    }

    /** Run validation and render results */
    validate(grid: GridCell[][], entrance: GridPos | null): Violation[] {
        this.violations = this.validator.validate(grid, entrance)
        this.render()
        return this.violations
    }

    /** Get current violations */
    getViolations(): Violation[] {
        return this.violations
    }

    /** Check if there are any errors (dungeon unplayable) */
    hasErrors(): boolean {
        return this.violations.some(v => v.severity === 'error')
    }

    private renderEmpty(): void {
        this.container.innerHTML = '<div class="validator-empty">Generate a dungeon to run validation</div>'
    }

    private render(): void {
        if (this.violations.length === 0) {
            this.container.innerHTML = `
                <div class="validator-panel">
                    <div class="validator-empty" style="color: var(--green);">
                        &#x2714; All 12 regulations pass
                    </div>
                </div>
            `
            return
        }

        const errors = this.violations.filter(v => v.severity === 'error').length
        const warnings = this.violations.filter(v => v.severity === 'warning').length
        const infos = this.violations.filter(v => v.severity === 'info').length

        const parts: string[] = []
        if (errors > 0) parts.push(`${errors} error${errors > 1 ? 's' : ''}`)
        if (warnings > 0) parts.push(`${warnings} warning${warnings > 1 ? 's' : ''}`)
        if (infos > 0) parts.push(`${infos} info`)

        this.container.innerHTML = `
            <div class="validator-panel">
                <div class="validator-count">${parts.join(', ')}</div>
                ${this.violations.map((v, i) => `
                    <div class="validator-item" data-violation-idx="${i}" title="Click to highlight affected cells">
                        <span class="validator-badge ${v.severity}">${v.severity}</span>
                        <span class="validator-msg">${v.message}</span>
                    </div>
                `).join('')}
            </div>
        `

        // Wire click → highlight cells
        this.container.querySelectorAll<HTMLElement>('.validator-item').forEach(el => {
            el.addEventListener('click', () => {
                const idx = parseInt(el.dataset.violationIdx ?? '-1')
                const v = this.violations[idx]
                if (v?.cells.length > 0) {
                    this.callbacks.onCellsHighlighted?.(v.cells)
                }
            })
        })
    }
}
